import { CARD_TO_CARD, IGateway } from 'utilities/hooks/useIsDefaultGateway'

/**
 * «روش پرداخت» — one picker, used by every pay screen.
 *
 * Before this the four pay screens each rendered their own bare row of logo
 * `<img>`s with a green border on the selected one: no names, no explanation,
 * different sizes and different spacing on each screen. That is the "شکل و
 * شمایلش هم یکی نیست" half of the report. It also made کارت‌به‌کارت
 * indistinguishable from an instant bank gateway, which matters — one of them
 * finishes in fifteen seconds and the other asks you to go and make a transfer
 * by hand. So each option carries its name and, where it changes what the user
 * is about to do, a line saying what happens next.
 */

const HINTS: Record<string, string> = {
    [CARD_TO_CARD]:
        'مبلغ را از اپ بانکی خودتان کارت‌به‌کارت می‌کنید؛ تایید آن ممکن است کمی طول بکشد.',
}

export default function GatewayPicker({
    gateways,
    selectedCode,
    onSelect,
}: {
    gateways: IGateway[]
    selectedCode: string | null
    onSelect: (code: string) => void
}) {
    if (gateways.length === 0) return null

    return (
        <div role="radiogroup" aria-label="روش پرداخت">
            <p className="mb-2 text-sm font-bold text-heading">روش پرداخت</p>
            <div className="flex flex-col gap-2">
                {gateways.map((gateway) => {
                    const isSelected = selectedCode === gateway.unique_code
                    const hint = HINTS[gateway.unique_code]
                    return (
                        <button
                            key={gateway.unique_code}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            onClick={() => onSelect(gateway.unique_code)}
                            className={`flex w-full items-center gap-3 rounded-card border p-3 text-right transition-colors motion-reduce:transition-none ${
                                isSelected
                                    ? 'border-green-1 bg-brand-soft'
                                    : 'border-divider bg-white'
                            }`}
                        >
                            {/* A fixed tile: the logos are different aspect
                                ratios and unboxed they made every row a
                                different height. Skipped entirely without a
                                logo, so a gateway that has none does not
                                render an empty white box. */}
                            {gateway.img_url && (
                                <span className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-white">
                                    <img
                                        src={gateway.img_url}
                                        alt=""
                                        aria-hidden="true"
                                        className="max-h-full max-w-full object-contain"
                                    />
                                </span>
                            )}

                            <span className="min-w-0 flex-1">
                                <span
                                    className={`block text-[15px] font-bold ${
                                        isSelected
                                            ? 'text-brand-deep'
                                            : 'text-heading'
                                    }`}
                                >
                                    {gateway.name || gateway.unique_code}
                                </span>
                                {hint && (
                                    <span className="mt-[2px] block text-xs leading-5 text-text-secondary">
                                        {hint}
                                    </span>
                                )}
                            </span>

                            <span
                                className={`h-5 w-5 shrink-0 rounded-full border-2 ${
                                    isSelected
                                        ? 'border-green-1 bg-green-1'
                                        : 'border-stroke bg-white'
                                }`}
                                aria-hidden="true"
                            />
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
